import { useId, useState } from 'react'
import { TX_STATUSES, type TxStatus } from '../../api/types'
import { useT } from '../../i18n'
import { Button, SelectField, TextField } from '../../ui'
import {
  PAGE_SIZES, boundDate, boundTime, composeBound, nextDay, previousDay, secondBefore, type Filters,
} from './filters'
import screen from '../screen.module.css'
import styles from './transactions.module.css'

export interface FilterBarProps {
  readonly filters: Filters
  readonly onChange: (changes: Partial<Filters>) => void
  readonly onReset: () => void
  readonly busy: boolean
  /** Gateways this user may filter by, already scoped by the server: every
   *  gateway on the platform for an internal user, only the assigned ones for
   *  a biller user. Passed in rather than fetched here so this component stays
   *  presentational and the screen owns the loading state. */
  readonly gateways: ReadonlyArray<{ readonly gateway_id: string; readonly gateway_name: string }>
  /** Organisations an internal user in all-organisations mode may narrow to,
   *  of either kind: the API answers `?biller=` with the named organisation's
   *  own scope — a provider's gateways, a beneficiary's slices. Undefined
   *  hides the control entirely: a biller user is locked to their own
   *  organisation, and an internal user who has selected one in the sidebar
   *  has already scoped the whole session. */
  readonly billers?: ReadonlyArray<{ readonly biller_id: string; readonly name: string }>
  /** FR-38: a filter is offered only when the field it filters is in the
   *  list. False hides the gateway control; a `?gateway=` in the URL still
   *  narrows the query, it just cannot be changed from here. */
  readonly showGatewayFilter: boolean
  /** FR-42: the free-text beneficiary filter exists exactly when the page
   *  exposes the identity: only ever to an internal user acting for a
   *  beneficiary organisation (AB-40). */
  readonly showBeneficiaryFilter: boolean
  /** MPAY-33571: the page's `filter_options`. Every value within the scope and
   *  the dates, so a filter that matches nothing keeps its own list. */
  readonly serviceLabels: readonly string[]
  readonly serviceTypes: readonly string[]
  /** FR-38: service type is grantable, so is its filter. */
  readonly showServiceTypeFilter: boolean
  /** The organisation's settlement cut-off ("HH:MM"), when it has one:
   *  enables the "Settlement day" checkbox, which only composes the bounds. */
  readonly settlementCutOff?: string
}

/** The whole-day defaults of the two time selectors. */
const FROM_TIME_DEFAULT = '00:00:00'
const TO_TIME_DEFAULT = '23:59:59'

/**
 * Every field applies on submit — nothing is applied as it is typed.
 *
 * The transaction-ID and PTN fields used to fire a debounced query on each
 * pause in typing. That was reversed deliberately: every one of those is a
 * billed BigQuery query for a filter the user had not finished expressing, and
 * a 20-character reference typed by hand could cost several. One Apply is one
 * query, and it matches how every other field in this bar already behaves.
 *
 * The whole bar is a `<form>`, so Enter submits from any field — which is what
 * someone pasting a reference will press regardless of what the buttons say.
 */
export function FilterBar({
  filters, onChange, onReset, busy, gateways, billers, showGatewayFilter, showBeneficiaryFilter, settlementCutOff,
  serviceLabels, serviceTypes, showServiceTypeFilter,
}: FilterBarProps) {
  const t = useT()
  const statusLegendId = useId()

  // Draft state for the submit-on-apply fields, re-seeded when the filters
  // change from elsewhere — a Reset, or the back button restoring a URL.
  //
  // Adjusted DURING RENDER rather than in an effect. React documents this
  // pattern for exactly this case: a setState in an effect commits the stale
  // render first and then immediately renders again, so the user sees one
  // frame of the previous values. Comparing a signature rather than the
  // `filters` object is what makes it terminate — `parseFilters` returns a new
  // object every render, so an identity check would loop forever.
  // Settlement mode is read off the bounds themselves: From at the cut-off
  // and To a second before it is exactly what the checkbox composes, so the
  // URL carries ordinary timed bounds and the form reads them back as
  // settlement days (From shifted forward one day).
  const settlementFrom = settlementCutOff ? `${settlementCutOff}:00` : ''
  const settlementTo = settlementCutOff ? secondBefore(settlementCutOff) : ''
  const seedFromFilters = () => {
    // One settlement day exactly: From on the previous day at the cut-off,
    // To on the day a second before it. Anything else — a range of
    // settlement days typed into the URL, say — reads back as plain bounds.
    const settlement =
      !!settlementCutOff && boundTime(filters.from) === settlementFrom && boundTime(filters.to) === settlementTo &&
      nextDay(boundDate(filters.from)) === boundDate(filters.to)
    return {
      from: settlement ? boundDate(filters.to) : boundDate(filters.from),
      fromTime: boundTime(filters.from) || FROM_TIME_DEFAULT,
      to: boundDate(filters.to), toTime: boundTime(filters.to) || TO_TIME_DEFAULT,
      settlement,
    min: filters.min, max: filters.max,
    ptn: filters.ptn, providerTxid: filters.providerTxid, statuses: filters.statuses,
    gateways: filters.gateways, biller: filters.biller, beneficiary: filters.beneficiary,
    serviceType: filters.serviceType, serviceLabel: filters.serviceLabel,
    }
  }

  // The cut-off is part of the signature: switching to an organisation
  // without one must drop settlement mode, not carry it over.
  const signature = [
    filters.from, filters.to, filters.min, filters.max,
    filters.ptn, filters.providerTxid, filters.statuses.join(','), filters.gateways.join(','),
    filters.biller, filters.beneficiary, filters.serviceType, filters.serviceLabel, settlementCutOff ?? '',
  ].join('|')
  const [seededFrom, setSeededFrom] = useState(signature)
  const [draft, setDraft] = useState(seedFromFilters)
  if (seededFrom !== signature) {
    setSeededFrom(signature)
    setDraft(seedFromFilters())
  }

  // Never render settlement mode without a cut-off to compose with.
  const settlementOn = draft.settlement && !!settlementCutOff

  const toggleStatus = (status: TxStatus) =>
    setDraft((d) => ({
      ...d,
      statuses: d.statuses.includes(status)
        ? d.statuses.filter((s) => s !== status)
        : [...d.statuses, status],
    }))

  return (
    <form
      className={styles.filters}
      onSubmit={(event) => {
        event.preventDefault()
        onChange({
          // Settlement day D = D-1 at the cut-off through D a second before it.
          from: settlementOn && settlementCutOff
            ? composeBound(previousDay(draft.to), settlementFrom, 'from')
            : composeBound(draft.from, draft.fromTime, 'from'),
          to: settlementOn && settlementCutOff
            ? composeBound(draft.to, settlementTo, 'to')
            : composeBound(draft.to, draft.toTime, 'to'),
          min: draft.min, max: draft.max,
          ptn: draft.ptn.trim(), providerTxid: draft.providerTxid.trim(), statuses: draft.statuses,
          gateways: draft.gateways, biller: draft.biller, beneficiary: draft.beneficiary.trim(),
          serviceType: draft.serviceType, serviceLabel: draft.serviceLabel,
        })
      }}
    >
      {settlementOn ? (
        <div className={styles.filterField}>
          {/* One report, one day: the settlement day stands for both bounds. */}
          <TextField
            label={t('tx.settlementDayOnly')} type="date" value={draft.to}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value, to: e.target.value }))}
          />
        </div>
      ) : (
        <div className={styles.filterField}>
          <TextField
            label={t('tx.from')} type="date" value={draft.from}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
          />
        </div>
      )}
      {!settlementOn && (
        <div className={styles.filterField}>
          {/* A time per bound (FR-41). Left at its whole-day default the bound
              travels as a bare date, exactly as before. In settlement mode the
              cut-off decides the times, so the selectors go away. */}
          <TextField
            label={t('tx.fromTime')} type="time" step={1} value={draft.fromTime}
            onChange={(e) => setDraft((d) => ({ ...d, fromTime: e.target.value }))}
          />
        </div>
      )}
      {!settlementOn && (
        <div className={styles.filterField}>
          <TextField
            label={t('tx.to')} type="date" value={draft.to}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            // Prevents the invalid range at the source rather than correcting it
            // after a rejected request.
            min={draft.from}
          />
        </div>
      )}
      {!settlementOn && (
        <div className={styles.filterField}>
          <TextField
            label={t('tx.toTime')} type="time" step={1} value={draft.toTime}
            onChange={(e) => setDraft((d) => ({ ...d, toTime: e.target.value }))}
          />
        </div>
      )}
      {settlementCutOff && (
        <div className={styles.filterWide}>
          {/* Ticked, From and To are settlement days and the cut-off decides
              the times; unticked, the bounds go back to whole days. Nothing
              here reaches the API — the checkbox only composes the bounds. */}
          <label className={styles.statusOption}>
            <input
              type="checkbox"
              checked={draft.settlement}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  settlement: e.target.checked,
                  // Ticking: the day is To's; unticking: the day becomes both
                  // bounds, whole days.
                  from: d.to,
                  fromTime: FROM_TIME_DEFAULT,
                  toTime: TO_TIME_DEFAULT,
                }))
              }
            />
            {t('tx.settlementDay', { cutOff: settlementCutOff })}
          </label>
          <p className={screen.hint}>{t('tx.settlementDayHint', { cutOff: settlementCutOff })}</p>
        </div>
      )}
      {/* A fieldset, not a multi-select: statuses are independent toggles, and
          a screen reader announces the group name with each checkbox. */}
      <fieldset className={styles.statusGroup} aria-labelledby={statusLegendId}>
        <span className={styles.statusLegend} id={statusLegendId}>
          {t('tx.status')}
        </span>
        <div className={styles.statusOptions}>
          {TX_STATUSES.map((status) => (
            <label key={status} className={styles.statusOption}>
              <input
                type="checkbox"
                checked={draft.statuses.includes(status)}
                onChange={() => toggleStatus(status)}
              />
              {t(`tx.status.${status}` as 'tx.status.SUCCESS')}
            </label>
          ))}
        </div>
      </fieldset>

      <div className={styles.filterField}>
        <TextField
          label={t('tx.amountMin')} type="number" min="0" inputMode="decimal" value={draft.min}
          onChange={(e) => setDraft((d) => ({ ...d, min: e.target.value }))}
        />
      </div>
      <div className={styles.filterField}>
        <TextField
          label={t('tx.amountMax')} type="number" min="0" inputMode="decimal" value={draft.max}
          onChange={(e) => setDraft((d) => ({ ...d, max: e.target.value }))}
        />
      </div>
      <div className={styles.filterField}>
        <TextField
          label={t('tx.ptn')} value={draft.ptn}
          onChange={(e) => setDraft((d) => ({ ...d, ptn: e.target.value }))}
        />
      </div>
      <div className={styles.filterField}>
        <TextField
          label={t('tx.providerTxid')} value={draft.providerTxid} maxLength={255}
          onChange={(e) => setDraft((d) => ({ ...d, providerTxid: e.target.value }))}
        />
      </div>
      {/* A single-select rather than a checkbox group: an internal user's list
          is every gateway on the platform, which is far too long to render as
          toggles. The URL still accepts several (`?gateway=a,b`) and the API
          honours them, so nothing is lost for anyone who wants a combination —
          it just is not the common case this control optimises for. */}
      {showGatewayFilter && (
      <div className={styles.filterWide}>
        <SelectField
          label={t('tx.gateway')}
          value={draft.gateways[0] ?? ''}
          onChange={(e) =>
            setDraft((d) => ({ ...d, gateways: e.target.value ? [e.target.value] : [] }))
          }
        >
          <option value="">{t('tx.gateway.all')}</option>
          {gateways.map((g) => (
            <option key={g.gateway_id} value={g.gateway_id}>
              {g.gateway_name}
            </option>
          ))}
          {/* A gateway pinned by a pasted URL but absent from this user's list
              is still shown as the current value, so the control never
              silently disagrees with what the table is filtered by. */}
          {draft.gateways[0] && !gateways.some((g) => g.gateway_id === draft.gateways[0]) && (
            <option value={draft.gateways[0]}>{draft.gateways[0]}</option>
          )}
        </SelectField>
      </div>
      )}

      {/* MPAY-33571. Single selects, like the gateway's; the API also takes
          several. A value pinned by a pasted URL but absent from the options
          stays visible, so the control never disagrees with the table. */}
      <div className={styles.filterWide}>
        <SelectField
          label={t('tx.serviceLabel')}
          value={draft.serviceLabel}
          onChange={(e) => setDraft((d) => ({ ...d, serviceLabel: e.target.value }))}
        >
          <option value="">{t('tx.serviceLabel.all')}</option>
          {serviceLabels.map((label) => (
            <option key={label} value={label}>{label}</option>
          ))}
          {draft.serviceLabel && !serviceLabels.includes(draft.serviceLabel) && (
            <option value={draft.serviceLabel}>{draft.serviceLabel}</option>
          )}
        </SelectField>
      </div>
      {showServiceTypeFilter && (
        <div className={styles.filterField}>
          <SelectField
            label={t('tx.serviceType')}
            value={draft.serviceType}
            onChange={(e) => setDraft((d) => ({ ...d, serviceType: e.target.value }))}
          >
            <option value="">{t('tx.serviceType.all')}</option>
            {serviceTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
            {draft.serviceType && !serviceTypes.includes(draft.serviceType) && (
              <option value={draft.serviceType}>{draft.serviceType}</option>
            )}
          </SelectField>
        </div>
      )}

      {showBeneficiaryFilter && (
        <div className={styles.filterField}>
          <TextField
            label={t('tx.beneficiary')}
            value={draft.beneficiary}
            maxLength={64}
            description={t('tx.beneficiary.hint')}
            onChange={(e) => setDraft((d) => ({ ...d, beneficiary: e.target.value }))}
          />
        </div>
      )}

      {billers && (
        <div className={styles.filterWide}>
          <SelectField
            label={t('tx.biller')}
            value={draft.biller}
            onChange={(e) => setDraft((d) => ({ ...d, biller: e.target.value }))}
          >
            <option value="">{t('tx.biller.all')}</option>
            {billers.map((b) => (
              <option key={b.biller_id} value={b.biller_id}>
                {b.name}
              </option>
            ))}
            {/* Same rule as the gateway select: a code pinned by a pasted URL
                but absent from the list stays visible as the current value. */}
            {draft.biller && !billers.some((b) => b.biller_id === draft.biller) && (
              <option value={draft.biller}>{draft.biller}</option>
            )}
          </SelectField>
        </div>
      )}

      <div className={styles.filterField}>
        <SelectField
          label={t('tx.pageSize')} value={String(filters.size)}
          onChange={(e) => onChange({ size: Number(e.target.value) })}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </SelectField>
      </div>

      <div className={styles.filterActions}>
        {/* Deliberately NOT `loading={busy}`: a loading Button is a disabled
            Button, and disabling Apply while a query is in flight means the
            user cannot change filters during the 1-3s a BigQuery-backed
            request will take — which is precisely when they most want to.
            It also makes the abort-on-filter-change path unreachable from
            this button. The in-flight state is shown by dimming the table
            instead, and re-submitting an unchanged filter set is free
            because TanStack Query dedupes on the key. */}
        <Button type="submit" aria-busy={busy || undefined}>
          {t('tx.apply')}
        </Button>
        <Button type="button" variant="secondary" onClick={onReset}>
          {t('tx.reset')}
        </Button>
      </div>
    </form>
  )
}
